/**
 * Mapping automatique Route → Étape de Funnel
 *
 * Configuration pour tracker automatiquement les étapes de funnel
 * en fonction des routes Vue.js visitées.
 *
 * Utilise les noms de routes du router pour plus de robustesse.
 */

export interface RouteLocation {
  name?: string | symbol | null;
  path?: string;
  params?: Record<string, string | string[]>;
  query?: Record<string, string | string[] | null | undefined | (string | null)[]>;
}

export interface RouteStepMapping {
  funnel: string;
  step: string;
  extractData?: (route: RouteLocation) => Record<string, unknown>;
}

/**
 * Mapping routes → étapes de funnel
 * Clé = nom de la route Vue.js
 * Valeur = configuration de l'étape funnel
 */
export const ROUTE_TO_FUNNEL_MAP: Record<string, RouteStepMapping> = {
  // 🎣 FUNNEL RÉSERVATION (Mini-site public)
  "public-lakes": {
    funnel: "reservation",
    step: "landing_page_vue",
    extractData: (route) => ({
      org_slug: route.params?.['orgSlug'],
    }),
  },

  "public-lake-details": {
    funnel: "reservation",
    step: "lake_details_vue",
    extractData: (route) => ({
      lake_id: route.params?.['lakeId'],
      org_slug: route.params?.['orgSlug'],
    }),
  },

  "public-reservation": {
    funnel: "reservation",
    step: "formulaire_renseigne", // Arrivée sur page de réservation
    extractData: (route) => ({
      lake_id: route.params?.['lakeId'],
      org_slug: route.params?.['orgSlug'],
    }),
  },

  "public-reservation-success": {
    funnel: "reservation",
    step: "paiement_valide",
    extractData: (route) => ({
      org_slug: route.params?.['orgSlug'],
    }),
  },

  // 🚀 FUNNEL ONBOARDING (Gestionnaire)
  "manager-dashboard": {
    funnel: "onboarding",
    step: "manager_dashboard_vue",
  },

  onboarding: {
    funnel: "onboarding",
    step: "onboarding_started",
  },

  "lakes-list": {
    funnel: "onboarding",
    step: "lakes_management_vue",
  },

  "lake-manage": {
    funnel: "onboarding",
    step: "lake_configuration_vue",
    extractData: (route) => ({
      lake_id: route.params?.['lakeId'],
    }),
  },

  "lake-fishing-spots": {
    funnel: "onboarding",
    step: "fishing_spots_management_vue",
  },

  billing: {
    funnel: "onboarding",
    step: "billing_setup_vue",
  },

  // ⚡ FUNNEL ACTIVATION (Fonctionnalités avancées)
  reservations: {
    funnel: "activation",
    step: "reservations_consultation",
  },

  "reservation-options": {
    funnel: "activation",
    step: "reservation_options_vue",
  },

  settings: {
    funnel: "activation",
    step: "settings_consultation",
  },

  users: {
    funnel: "activation",
    step: "users_management_vue",
  },

  projects: {
    funnel: "activation",
    step: "projects_consultation",
  },

  faq: {
    funnel: "activation",
    step: "faq_consultation",
  },

  // 🔧 ADMIN (Super Admin)
  "admin-dashboard": {
    funnel: "activation",
    step: "admin_dashboard_vue",
  },

  // 🔐 AUTH (Connexion/Inscription)
  login: {
    funnel: "onboarding",
    step: "login_page_vue",
  },

  signup: {
    funnel: "onboarding",
    step: "signup_page_vue",
  },

  "oauth-callback": {
    funnel: "onboarding",
    step: "oauth_callback_processing",
  },
};

/**
 * Routes à ignorer pour le tracking de funnel
 * (pages statiques, erreurs, etc.)
 */
export const IGNORED_ROUTES = new Set([
  "landing",
  "legal",
  "cgv",
  "mentions",
  "terms",
  "privacy",
  "404",
  "forbidden",
  "recover-password",
  "recover-password-email",
]);

/**
 * Obtenir la configuration funnel pour une route
 */
export function getFunnelMappingForRoute(
  routeName: string,
): RouteStepMapping | null {
  if (IGNORED_ROUTES.has(routeName)) {
    return null;
  }

  return ROUTE_TO_FUNNEL_MAP[routeName] || null;
}

/**
 * Extraire les données contextuelles d'une route
 */
export function extractRouteData(
  route: RouteLocation,
  mapping: RouteStepMapping,
): Record<string, unknown> {
  const baseData = {
    route_name: route.name,
    route_path: route.path,
    route_params: route.params,
    route_query: route.query,
  };

  if (mapping.extractData) {
    return {
      ...baseData,
      ...mapping.extractData(route),
    };
  }

  return baseData;
}
